/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { Grid, Box, Container, useMediaQuery, useTheme, TextField, Typography, Button } from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, useAppDispatch } from "@store/store";
import {
  useLazyGetLoanCreditUseQuery,
  useLazyPostLoanUseQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { ProgressBar } from "@components/ProgressBar";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { setLoanStatus } from "@store/slices/registerSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";

const SCREEN_STATUS = "CSUDC";

const UsoDeCredito = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getLoanCreditUse] = useLazyGetLoanCreditUseQuery();
  const [triggerSaveUseSelected] = useLazyPostLoanUseQuery();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const formik = useFormik({
    initialValues: {
      uses: [] as string[],
      reason: "",
      activity: "",
    },
    validationSchema: yup.object().shape({
      reason: yup.string().required("Campo requerido"),
      activity: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        setSubmitting(true);

        const resources = {
          reason: values.reason,
          activity: values.activity,
          business_sector_id: null,
          resource_uses_identifiers: [],
        };

        await triggerSaveUseSelected({
          loanId: applicationData.id,
          body: resources,
        }).unwrap();

        // const isPFAE = applicationData.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code;
        // const next_status = isPFAE ? STATUS_CATALOG.presencia_digital_pfae.code : STATUS_CATALOG.presencia_digital.code;
        // const next_url = isPFAE ? STATUS_CATALOG.presencia_digital_pfae.url : STATUS_CATALOG.presencia_digital.url;

        await triggerPostNextStage({
          applicationId: applicationData.id,
          statusCode: nextStatus?.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setLoanStatus(nextStatus?.code));

        navigate(nextStatus?.url);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "UsoDeCredito",
            values: values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      const loan_credit_use = await getLoanCreditUse(applicationData.id).unwrap();

      if (loan_credit_use) {
        formik.setFieldValue("reason", loan_credit_use?.reason);
        formik.setFieldValue("activity", loan_credit_use?.activity);
        setTimeout(() => {
          formik.validateForm();
        }, 200);
      }
    };

    fetchData();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.uso_de_credito?.titulo || "Selección de uso de crédito"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <TextField
                id="reason"
                name="reason"
                label="Describe el destino del crédito"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.reason}
                error={formik.touched.reason && Boolean(formik.errors.reason)}
                helperText={formik.touched.reason && formik.errors.reason}
                disabled={formik.isSubmitting}
                inputProps={{ maxLength: 150 }}
                multiline
                rows={4}
              />
              <TextField
                id="activity"
                name="activity"
                label="Describe la actividad de la empresa"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.activity}
                error={formik.touched.activity && Boolean(formik.errors.activity)}
                helperText={formik.touched.activity && formik.errors.activity}
                disabled={formik.isSubmitting}
                inputProps={{ maxLength: 150 }}
                multiline
                rows={4}
              />

              <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button type="submit" variant="default" disabled={!formik.isValid || formik.isSubmitting}>
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default UsoDeCredito;
